import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Server, Search, RefreshCw, Copy } from 'lucide-react';
import { Toolbar, PageHeader, Container } from '@vnetwork/ui/layout';
import { DataTable, StatusPill, EmptyState, type ColumnDef } from '@vnetwork/ui/data';
import { Button, Input, Skeleton, IconButton } from '@vnetwork/ui/primitives';
import { useToast } from '@vnetwork/ui/feedback';
import {
  useInstanceList,
  useInstanceSummaries,
  useInstanceDetails,
  type VmwareInstance,
  type VmwareInstanceSummary,
} from '@/api/vmware/instances';
import { vmTone, vmLabel, vmBucket, copyToClipboard, pickPublicIpv4 } from '../shared';

/**
 * VMware VM list.
 *
 * Power state comes from summary.powerState (vSphere camelCase: "poweredOn" /
 * "poweredOff" / "suspended") because instance.status is the @mgmt provisioning
 * status ("active"), NOT the power state. We normalise to UPPER_SNAKE via vmTone /
 * vmLabel helpers which already handle both camelCase and UPPER_SNAKE variants.
 *
 * IP comes from summary.summaryItems[label=summaryView.ipAddressLabel] because the
 * list endpoint does not include an ip field on each instance row. We pick the first
 * public IPv4 (skip link-local fe80:: and all plain IPv6), matching @console logic.
 *
 * Hostname comes from summaryItems[label=summaryView.dnsNameLabel].
 */
export function InstanceList() {
  const navigate = useNavigate();
  const { serviceId } = useParams<{ serviceId: string }>();
  const { data: instances = [], isLoading, isError, refetch } = useInstanceList(serviceId);
  const [search, setSearch] = useState('');
  const base = `/services/compute/${serviceId}`;

  // Batch summaries — used for power state pill, hostname, and IP.
  const instanceIds = useMemo(() => instances.map((i) => i.id), [instances]);
  const summaries = useInstanceSummaries(serviceId, instanceIds);
  // Batch details — provides metadata.cpu/memory for vCPU and Memory columns.
  const details = useInstanceDetails(serviceId, instanceIds);

  const health = useMemo(() => {
    const b = { running: 0, stopped: 0, error: 0, other: 0 };
    for (const i of instances) {
      const s = summaries[i.id];
      // Power state from summary.powerState (camelCase vSphere) — vmBucket handles both.
      b[vmBucket(s?.powerState ?? i.metadata?.power_state)]++;
    }
    return b;
  }, [instances, summaries]);

  /** Hostname from summaryItems dnsNameLabel (mirrors @console VMwareDashboard). */
  const hostnameFor = (id: string): string => {
    const s = summaries[id];
    const item = s?.summaryItems?.find((x) => x.label === 'summaryView.dnsNameLabel');
    const val = item?.value;
    if (Array.isArray(val)) return val[0] ?? '';
    return typeof val === 'string' ? val : '';
  };

  /**
   * IP from summaryItems ipAddressLabel (array of strings). Pick first public IPv4
   * (skip link-local fe80:: and any plain IPv6 addresses).
   * Mirrors ipAddressExtract logic in @console ManageOverview (~L534).
   */
  const ipFor = (id: string): string => {
    const s = summaries[id];
    return pickPublicIpv4(s);
  };

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return instances;
    return instances.filter((i) => {
      const hostname = hostnameFor(i.id).toLowerCase();
      const name = (i.name ?? i.id).toLowerCase();
      const ip = ipFor(i.id).toLowerCase();
      return name.includes(q) || ip.includes(q) || hostname.includes(q);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [instances, search, summaries]);

  const columns = useMemo<ColumnDef<VmwareInstance, unknown>[]>(
    () => [
      {
        // Instance ID column — shows the service UUID (row.id), matching @console's
        // "Instance ID" = service_uuid. Row click also navigates by row.id via getRowId.
        id: 'id',
        header: 'Instance ID',
        cell: ({ row }) => (
          <code style={{ fontSize: 'var(--vn-font-size-sm)', color: 'var(--vn-fg-muted)' }}>
            {String(row.original.id ?? '—')}
          </code>
        ),
      },
      {
        // Hostname from summaryItems.dnsNameLabel — mirrors @console "Name" column.
        id: 'hostname',
        header: 'Hostname',
        cell: ({ row }) => {
          const hn = hostnameFor(row.original.id);
          return (
            <span style={{ fontWeight: 500 }}>
              {hn || row.original.name || row.original.id}
            </span>
          );
        },
      },
      {
        id: 'status',
        header: 'State',
        enableSorting: false,
        cell: ({ row }) => {
          const s = summaries[row.original.id];
          // Use summary.powerState (vSphere camelCase) as primary source.
          // Fallback to instance.metadata.power_state from the detail cache.
          // Never use instance.status — that is the @mgmt provisioning status.
          const st = s?.powerState ?? row.original.metadata?.power_state;
          return <StatusPill tone={vmTone(st)}>{vmLabel(st)}</StatusPill>;
        },
      },
      {
        id: 'ip',
        header: 'IP',
        enableSorting: false,
        cell: ({ row }) => {
          const ip = ipFor(row.original.id);
          return ip ? <IpCell ip={ip} /> : <span style={{ color: 'var(--vn-fg-subtle)' }}>—</span>;
        },
      },
      {
        id: 'cpu',
        header: 'vCPU',
        enableSorting: false,
        cell: ({ row }) => {
          // Use batch-fetched detail for metadata.cpu.count — list rows have no metadata.
          const cpu = details[row.original.id]?.metadata?.cpu?.count;
          return <span style={{ color: 'var(--vn-fg-muted)' }}>{cpu ?? '—'}</span>;
        },
      },
      {
        id: 'memory',
        header: 'Memory',
        enableSorting: false,
        cell: ({ row }) => {
          // Use batch-fetched detail for metadata.memory.size_MiB — list rows have no metadata.
          const mib = details[row.original.id]?.metadata?.memory?.size_MiB;
          return (
            <span style={{ color: 'var(--vn-fg-muted)' }}>
              {mib != null ? `${Math.round(mib / 1024)} GiB` : '—'}
            </span>
          );
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [summaries, details],
  );

  return (
    <Container size="xl">
      <PageHeader
        icon={<Server size={22} />}
        title={`Instances${instances.length ? ` (${instances.length})` : ''}`}
        description="VMware virtual machines allocated to this service."
        actions={
          <Button variant="secondary" startIcon={<RefreshCw size={15} />} onClick={() => refetch()}>
            Refresh
          </Button>
        }
      />

      {instances.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 'var(--vn-space-3)', marginBottom: 'var(--vn-space-4)' }}>
          <HealthChip label="Running" value={health.running} tone="success" />
          <HealthChip label="Powered off" value={health.stopped} tone="muted" />
          <HealthChip label="Error" value={health.error} tone="danger" />
          <HealthChip label="Other" value={health.other} tone="warning" />
        </div>
      )}

      <Toolbar
        start={
          <Input
            placeholder="Find instances by name, hostname or IP"
            startSlot={<Search />}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ maxWidth: 360 }}
          />
        }
      />

      {isError ? (
        <EmptyState
          icon={<Server size={28} />}
          title="Couldn't load instances"
          description="Something went wrong fetching this service's virtual machines."
          actions={<Button variant="secondary" startIcon={<RefreshCw size={15} />} onClick={() => refetch()}>Retry</Button>}
        />
      ) : isLoading ? (
        <div style={{ display: 'grid', gap: 'var(--vn-space-3)' }}>
          {Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} style={{ height: 48 }} />)}
        </div>
      ) : (
        <DataTable
          columns={columns}
          data={rows}
          getRowId={(r) => r.id}
          onRowClick={(r) => navigate(`${base}/instances/${r.id}`)}
          footer={`Showing ${rows.length} of ${instances.length}`}
          emptyState={
            instances.length === 0 ? (
              <EmptyState icon={<Server size={28} />} title="No instances" description="No virtual machines are provisioned for this service." />
            ) : 'No instances match your search.'
          }
        />
      )}
    </Container>
  );
}

// ---- Sub-components ---------------------------------------------------------

function HealthChip({ label, value, tone }: { label: string; value: number; tone: 'success' | 'muted' | 'danger' | 'warning' }) {
  const color = tone === 'success' ? 'var(--vn-success-solid)' : tone === 'danger' ? 'var(--vn-danger-solid)' : tone === 'warning' ? 'var(--vn-warning-solid)' : 'var(--vn-fg-muted)';
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'var(--vn-space-2)', padding: 'var(--vn-space-2) var(--vn-space-3)', border: '1px solid var(--vn-border-default)', borderRadius: 'var(--vn-radius-md)', background: 'var(--vn-bg-surface)' }}>
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--vn-space-2)', color: 'var(--vn-fg-muted)', fontSize: 'var(--vn-font-size-sm)' }}>
        <span style={{ width: 8, height: 8, borderRadius: 'var(--vn-radius-full)', background: color }} />
        {label}
      </span>
      <span style={{ fontWeight: 700, fontSize: 'var(--vn-font-size-lg)' }}>{value}</span>
    </div>
  );
}

function IpCell({ ip }: { ip: string }) {
  const { show } = useToast();
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--vn-space-1)' }}>
      <span style={{ fontVariantNumeric: 'tabular-nums' }}>{ip}</span>
      <IconButton variant="ghost" size="sm" aria-label={`Copy ${ip}`}
        onClick={async (e) => {
          e.stopPropagation();
          if (await copyToClipboard(ip)) show({ tone: 'success', title: 'IP copied' });
        }}
      >
        <Copy size={13} />
      </IconButton>
    </span>
  );
}

// Re-export keeps downstream imports working.
export type { VmwareInstanceSummary };
